// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { AxiosError } from 'axios'
import { MessageConfig, useGlobalMessage } from '@teamnine/pcj-ui-v2'

type ErrorData = {
  meta?: {
    argument?: string
    cause?: string
  }
}

type ErrorHandlerOptions = {
  useMetaCause: boolean
}

export const errorHandler = (
  displayGlobalMessage: (displayMsg: MessageConfig) => void,
  error: AxiosError | Error | unknown,
  options?: ErrorHandlerOptions
) => {
  let message = 'Unexpected error. Please try again'
  if (error instanceof AxiosError) {
    if (error.response?.status === 403) {
      message = error.response?.data.msg
    } else if (
      options?.useMetaCause &&
      (error.response?.data as ErrorData).meta
    ) {
      const cause = (error.response?.data as ErrorData).meta?.cause
      message = cause ?? error.response?.data.msg
    } else {
      message = error.response?.data.msg ?? message
    }
  } else if (error instanceof Error) {
    message = error.message
  }

  displayGlobalMessage({
    heading: message,
    type: 'danger',
  })
}

export const useErrorHelper = () => {
  const { displayGlobalMessage } = useGlobalMessage()
  const basicErrorHandler = (error: AxiosError | Error | unknown) => {
    errorHandler(displayGlobalMessage, error)
  }

  const errorWithMetadataHandler = (error: AxiosError | unknown) => {
    errorHandler(displayGlobalMessage, error, { useMetaCause: true })
  }

  return {
    displayGlobalMessage,
    basicErrorHandler,
    errorWithMetadataHandler,
  }
}
